import { LoadableElement } from '@99designs/common/components/LoadableElement';
import {
    getSmartShootProject,
    getSmartShootProjectVariables,
} from '__generated__/getSmartShootProject';
import { handleGraphQLError } from 'common/utils/Apollo';
import gql from 'graphql-tag';
import BriefGetStartedContent from 'projects/components/SmartShoot/BriefGetStartedContent';
import BriefGetStartedUpsell from 'projects/components/SmartShoot/BriefGetStartedUpsell';
import BriefHero from 'projects/components/SmartShoot/BriefHero';
import { trackViewedBriefEvent } from 'projects/events';
import React from 'react';
import { Query } from 'react-apollo';

const query = gql`
    query getSmartShootProject($id: Int!) {
        smartShootProject(id: $id) {
            id
            brief {
                category {
                    key
                }
            }
            data {
                urls {
                    scheduling
                }
            }
        }
    }
`;

interface Props {
    projectId: string;
}

class SmartShootProjectGetStarted extends React.Component<Props> {
    componentDidMount() {
        trackViewedBriefEvent({ id: this.props.projectId, page: 'GettingStarted' });
    }

    render() {
        return (
            <Query<getSmartShootProject, getSmartShootProjectVariables>
                query={query}
                variables={{ id: parseInt(this.props.projectId) }}
            >
                {({ data, error, loading }) => {
                    if (loading) {
                        return <LoadableElement isLoading={true} variations={['relative']} />;
                    }

                    if (error) {
                        handleGraphQLError(error);
                    }

                    if (data && data.smartShootProject) {
                        return (
                            <React.Fragment>
                                {data.smartShootProject.brief!.category!.key === 'video-production'
                                    ? this.renderVideo(data.smartShootProject)
                                    : this.renderPhotography(data.smartShootProject)}
                            </React.Fragment>
                        );
                    }

                    return null;
                }}
            </Query>
        );
    }

    renderVideo(smartShootProject: any) {
        const heading = 'And we’re rollin’';
        this.updateDocumentTitle(heading);
        return (
            <>
                <BriefHero
                    heading={heading}
                    paragraph="We just sent your creative brief to SmartShoot's incredible network of professional filmmakers. Sit tight while our crew finds the perfect match for you. Look for an email from us shortly!"
                />
                <BriefGetStartedContent
                    imageSource={require(`images/smartshoot/confirmation/ss-video-confirmed-pig.png`)}
                    stepContent={[
                        {
                            icon: 'contacts-1',
                            heading: 'It’s a match!',
                            paragraph:
                                'Once we’ve found your perfect filmmaker, we’ll email you their contact information. You’ll hear from us within 72 hours.',
                        },
                        {
                            icon: 'sidebar-1',
                            heading: 'Get script-ready',
                            paragraph:
                                ' Magic takes time! After reviewing your brief, your filmmaker will send a draft script for review. You’ll collaborate with them to come up with a script that you’re both happy with. You’ll also confirm the date and time of your shoot.',
                        },
                        {
                            icon: 'promote-1',
                            heading: 'Lights, camera, action!',
                            paragraph:
                                'Your filmmaker will meet you on-location at the date and time you’ve chosen. Then, it’s time to roll the cameras! You’ll work together to create a video you love.',
                        },
                        {
                            icon: 'annotate-1',
                            heading: 'The editing room',
                            paragraph:
                                'In just a few days, your filmmaker will upload a rough cut of the video for review. You’ll provide feedback and get 1 (basic package) or 2 (premium package) stages of revisions.',
                        },
                        {
                            icon: 'video-1',
                            heading: 'The big premiere!',
                            paragraph:
                                '  Once you’ve approved the final cut, your can download your video! You’ll own all of the video files and full copyright.',
                        },
                    ]}
                    rescheduleUrl={smartShootProject.data.urls.scheduling}
                />
                <BriefGetStartedUpsell
                    heading="Need a photographer?"
                    paragraph="Work with a talented photographer to capture those important moments. Everything from business events, website images to even adventure and lifestyle"
                    imageSource={require(`images/smartshoot/confirmation/upsell/ss-video-confirmed-photos.png`)}
                    attributon="Photo shoot for Viscera clothing jewellery store."
                    href="/hire-a-photographer"
                    ctaText="Find out more"
                    color={'#47657C'}
                />
            </>
        );
    }

    renderPhotography(smartShootProject: any) {
        const heading = 'Your awesome photos are developing...';
        this.updateDocumentTitle(heading);
        return (
            <>
                <BriefHero
                    heading={heading}
                    paragraph="We just sent your creative brief to SmartShoot’s talented network of professional photographers. Sit tight while our crew finds the perfect match for you. Look for an email from us shortly!"
                />
                <BriefGetStartedContent
                    imageSource={require(`images/smartshoot/confirmation/ss-photo-confirmed-pig.png`)}
                    stepContent={[
                        {
                            icon: 'contacts-1',
                            heading: 'It’s a match!',
                            paragraph:
                                'Once we’ve found your perfect photographer, we’ll email you their contact information. You’ll hear from us within 72 hours.',
                        },
                        {
                            icon: 'video-1',
                            heading: 'Capture the moment',
                            paragraph:
                                ' Your photographer will meet you on-location at the date and time you’ve chosen. Then, it’s time to work together to create high-quality, amazing photos.',
                        },
                        {
                            icon: 'annotate-1',
                            heading: 'The editing room',
                            paragraph:
                                'In just a few days, your photographer will upload your edited photos for review. You’ll have the opportunity to provide feedback.',
                        },
                        {
                            icon: 'maximize-1',
                            heading: 'The big reveal!',
                            paragraph:
                                'Once you’ve approved the final images, you can download your entire photo gallery! You’ll own all of the digital files and full copyright.',
                        },
                    ]}
                    rescheduleUrl={smartShootProject.data.urls.scheduling}
                />
                <BriefGetStartedUpsell
                    heading="Need a video?"
                    paragraph="Whether it's an Instagram campaign, a product explainer or even a tour of your new digs, book a shoot with our talented filmmakers, and they'll create a custom video you'll love."
                    imageSource={require(`images/smartshoot/upsell/video.png`)}
                    attributon=""
                    href="/video-production"
                    ctaText="Find out more"
                    color={'#000000'}
                />
            </>
        );
    }

    updateDocumentTitle(heading: string) {
        document.title = `${heading} | 99designs`;
    }
}

export default SmartShootProjectGetStarted;
